import { useStore } from "@/store";
import { Permission } from "./config";
import { useCallback, useMemo } from "react";

// Permission kontrolü için hook
export const usePermission = () => {
  const { isStaff, permissionsArray } = useStore();

  const hasPermission = useCallback((permission: Permission): boolean => {
    // Company training profiles modülü admin kullanıcılar için her zaman açık.
    if (
      isStaff &&
      ((typeof permission.admin === "string" &&
        permission.admin.startsWith("COMPANY_TRAINING_PROFILES.")) ||
        (Array.isArray(permission.admin) &&
          permission.admin.some((perm) =>
            perm.startsWith("COMPANY_TRAINING_PROFILES."),
          )))
    ) {
      return true;
    }

    const requiredPermissions =
      isStaff && permission.staff ? permission.staff : permission.admin;
    if (!requiredPermissions) return false;
    const permissions = Array.isArray(requiredPermissions)
      ? requiredPermissions
      : [requiredPermissions];
    return permissions.every((perm) => permissionsArray.includes(perm));
  }, [isStaff, permissionsArray]);

  /** API'den gelen key ile direkt kontrol (örn. "ANNOUNCEMENTS.CREATE") */
  const hasPermissionKey = useCallback(
    (key: string): boolean => permissionsArray.includes(key),
    [permissionsArray],
  );

  return { hasPermission, hasPermissionKey };
};

/**
 * Per-education yönetici kontrolü: oturumdaki kullanıcı, verilen eğitimin
 * `managers` listesinde mi? EDUCATION_SUPERVISOR rolündeki bir kullanıcı bir
 * eğitime "Eğitim Yöneticisi" olarak atandığında, sadece o eğitim üzerinde
 * EDUCATION_MANAGER yetkilerine sahip olur — bu kontrol UI tarafında
 * butonların doğru gösterilmesi için kullanılır.
 *
 * Backend `EducationAccessGuard` zaten aynı kontrolü server-side yapar; bu hook
 * yalnızca görsel feedback amaçlıdır. Yeni API çağrısı YOK; mevcut education
 * detail response'undaki `managers` array'inden okur.
 */
export const useIsEducationManagerOf = (
  education: { managers?: Array<{ userID?: string | null }> | null } | null | undefined,
): boolean => {
  const currentUserId = useStore((s) => s.currentUser?.id);
  return useMemo(() => {
    if (!currentUserId) return false;
    const managers = education?.managers;
    if (!managers?.length) return false;
    return managers.some((m) => m?.userID === currentUserId);
  }, [currentUserId, education?.managers]);
};
